import { BaseMgSupplierCampaignItem } from '@retail/campaign/types';
import { useSearchFilters } from '@retail/products/context';
import { Filter, filterList } from '@shared/utils';
import { useMemo } from 'react';

interface UseItemsFilterProps {
  items: BaseMgSupplierCampaignItem[];
}

export const useItemsFilter = ({ items }: UseItemsFilterProps) => {
  const { filters } = useSearchFilters();

  const supplierFilter: Filter<BaseMgSupplierCampaignItem> = useMemo(
    () => ({
      active: !!filters.supplierNumbers?.length,
      predicate: (item) =>
        !!filters.supplierNumbers?.some((supNumber) => Number(supNumber) === item.supplier.id),
    }),
    [filters.supplierNumbers]
  );

  const productGroupsFilter: Filter<BaseMgSupplierCampaignItem> = useMemo(
    () => ({
      active: !!filters.productGroupNumbers?.length,
      predicate: (item) =>
        !!filters.productGroupNumbers?.some((groupNumber) =>
          item.productGroup.groupNumber.startsWith(groupNumber)
        ),
    }),
    [filters.productGroupNumbers]
  );

  const searchQueryFilter: Filter<BaseMgSupplierCampaignItem> = useMemo(
    () => ({
      active: !!filters.searchQuery,
      predicate: (item) =>
        !!(
          item.primaryText
            .toLocaleLowerCase()
            .startsWith(filters.searchQuery?.toLocaleLowerCase() ?? '') ||
          item.secondaryText
            ?.toLocaleLowerCase()
            .startsWith(filters.searchQuery?.toLocaleLowerCase() ?? '')
        ),
    }),
    [filters.searchQuery]
  );

  const filteredItems = useMemo(
    () =>
      filterList({
        filters: [supplierFilter, productGroupsFilter, searchQueryFilter],
        initList: items,
      }),
    [items, productGroupsFilter, searchQueryFilter, supplierFilter]
  );

  return { filteredItems };
};
